import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { DIMENSIONS } from '@optiflow/frame-recommendation';
import { RecommendationPreferenceMode, type RecommendationDimensionMode } from '@optiflow/types';
import { useCurrency } from '@/hooks/useCurrency';
import { useLocale } from '@/hooks/useLocale';
import { useBrandsQuery } from '@/modules/catalog/api/catalog.api';
import { formatList } from '@/utils/format';
import { hasBudget, hasSize, type CriteriaGroup, type RecommendationCriteria } from '../criteria';

interface Chip {
  group: CriteriaGroup;
  label: string;
}

/**
 * The applied filters at a glance, each removable on its own.
 *
 * Removing one is a single deliberate action, so it applies straight away; editing values happens in the filter panel.
 */
export function AppliedFilterChips({
  criteria,
  canReadBrands,
  onRemove,
  onClearAll,
}: {
  criteria: RecommendationCriteria;
  canReadBrands: boolean;
  onRemove: (group: CriteriaGroup) => void;
  onClearAll: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { format } = useCurrency();
  const brands = useBrandNames(canReadBrands && criteria.brandIds.length > 0);

  const list = (values: string[]) => formatList(values, locale);
  // Sizes say RANK where preferences say PREFER; both say ONLY for keeping only what matches.
  const withMode = (
    label: string,
    values: string[],
    mode: RecommendationPreferenceMode | RecommendationDimensionMode,
  ) =>
    t(
      mode === RecommendationPreferenceMode.ONLY
        ? 'frameRecommendations.chips.only'
        : 'frameRecommendations.chips.prefer',
      { label, values: list(values) },
    );

  const chips: Chip[] = [];

  if (hasBudget(criteria)) {
    const { budgetMinMinor: min, budgetMaxMinor: max } = criteria;
    chips.push({
      group: 'budget',
      label:
        min !== null && max !== null
          ? t('frameRecommendations.chips.budgetRange', { min: format(min), max: format(max) })
          : min !== null
            ? t('frameRecommendations.chips.budgetFrom', { min: format(min) })
            : t('frameRecommendations.chips.budgetUpTo', { max: format(max) }),
    });
  }

  if (hasSize(criteria)) {
    const parts = [
      ...(criteria.sizeShortcut ? [criteria.sizeShortcut] : []),
      ...DIMENSIONS.flatMap((dimension) => {
        const range = criteria.ranges[dimension];
        if (range.minMm === null && range.maxMm === null) return [];
        return [
          t('frameRecommendations.chips.range', {
            dimension: t(`frameRecommendations.dimensionShort.${dimension}`),
            min: range.minMm ?? '…',
            max: range.maxMm ?? '…',
          }),
        ];
      }),
    ];
    chips.push({
      group: 'size',
      label: withMode(t('frameRecommendations.filters.size.legend'), parts, criteria.dimensionMode),
    });
  }

  if (criteria.frameShapes.length > 0) {
    chips.push({
      group: 'frameShapes',
      label: withMode(
        t('frameRecommendations.filters.shape.legend'),
        criteria.frameShapes.map((shape) => t(`catalog.frameShape.${shape}`)),
        criteria.frameShapesMode,
      ),
    });
  }

  if (criteria.brandIds.length > 0) {
    chips.push({
      group: 'brandIds',
      label: withMode(
        t('frameRecommendations.filters.brand.legend'),
        criteria.brandIds.map(
          (id) => brands.get(id) ?? t('frameRecommendations.chips.unnamedBrand'),
        ),
        criteria.brandIdsMode,
      ),
    });
  }

  if (criteria.colours.length > 0) {
    chips.push({
      group: 'colours',
      label: withMode(
        t('frameRecommendations.filters.colour.legend'),
        criteria.colours,
        criteria.coloursMode,
      ),
    });
  }

  const catalogue = (
    group: 'materials' | 'rimTypes' | 'targetGroups',
    legend: string,
    values: string[],
  ) => {
    if (values.length > 0) {
      chips.push({
        group,
        label: t('frameRecommendations.chips.list', { label: legend, values: list(values) }),
      });
    }
  };
  catalogue(
    'materials',
    t('frameRecommendations.filters.material.legend'),
    criteria.materials.map((value) => t(`catalog.frameMaterial.${value}`)),
  );
  catalogue(
    'rimTypes',
    t('frameRecommendations.filters.rimType.legend'),
    criteria.rimTypes.map((value) => t(`catalog.rimType.${value}`)),
  );
  catalogue(
    'targetGroups',
    t('frameRecommendations.filters.targetGroup.legend'),
    criteria.targetGroups.map((value) => t(`catalog.targetGroup.${value}`)),
  );

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="sr-only">{t('frameRecommendations.chips.label')}</span>
      <ul className="flex min-w-0 flex-wrap gap-2">
        {chips.map((chip) => (
          <li
            key={chip.group}
            className="border-line text-ink inline-flex max-w-full min-h-9 items-center gap-1 rounded-full border bg-white ps-3 pe-1 text-sm"
          >
            <span className="min-w-0 break-words">{chip.label}</span>
            <button
              type="button"
              onClick={() => onRemove(chip.group)}
              aria-label={t('frameRecommendations.chips.remove', { label: chip.label })}
              className="text-muted hover:bg-canvas hover:text-ink grid size-7 shrink-0 place-items-center rounded-full"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onClearAll}
        className="text-brand-700 hover:text-brand-800 min-h-9 px-1 text-sm font-medium"
      >
        {t('frameRecommendations.filters.clear')}
      </button>
    </div>
  );
}

/** Brand names for the chips, from the same cached catalogue list the filter panel reads. */
function useBrandNames(enabled: boolean): Map<string, string> {
  const brands = useBrandsQuery({ enabled });
  return new Map((brands.data?.items ?? []).map((brand) => [brand.id, brand.name]));
}
